import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Sheet,
  Select,
  Option,
  IconButton,
  Textarea,
} from '@mui/joy';
import { Edit } from '@mui/icons-material';
import PageLayout from '../components/PageLayout';
import { supabase } from '../supabaseClient';
import useUser from '../context/AuthContext.jsx';

const Staff = () => {
  const { user } = useUser();
  const [users, setUsers] = useState([]);
  const [sortKey, setSortKey] = useState('display_name');
  const [sortAsc, setSortAsc] = useState(true);
  const [editingUserId, setEditingUserId] = useState(null);
  const [statusUpdates, setStatusUpdates] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    // Ensure correct filter syntax: exclude users where user_type === 'client'
    const { data, error } = await supabase
      .from('users')
      .select('id, display_name, email, status, status_note, user_type, department')
      .not('user_type', 'eq', 'client');

    if (error) {
      console.error('Failed to fetch users:', error);
    } else {
      setUsers(data);
    }
  };

  const handleSort = (key) => {
    if (sortKey === key) {
      setSortAsc(!sortAsc);
    } else {
      setSortKey(key);
      setSortAsc(true);
    }
  };

  const statusOrder = {
    'In Office': 1,
    'Remote': 2,
    'In Field': 3,
    'Out for the Day': 4,
  };

  const sortedUsers = [...users].sort((a, b) =>
    (a.display_name || '').localeCompare(b.display_name || '')
  );

  const handleStatusChange = async (id, newStatus) => {
    const updates = {
      status: newStatus,
      status_note: statusUpdates[`note_${id}`] || null,
    };
    setStatusUpdates((prev) => ({ ...prev, [id]: newStatus }));
    const { error } = await supabase
      .from('users')
      .update(updates)
      .eq('id', id);
    if (error) {
      console.error('Failed to update status:', error);
    } else {
      await fetchUsers();
      setEditingUserId(null);
    }
  };

  const handleNoteChange = (id, note) => {
    setStatusUpdates((prev) => ({ ...prev, [`note_${id}`]: note }));
  };

  const canEdit = (targetUser) => {
    if (!user || !targetUser) return false;
    const isAdmin =
      user.user_type === 'admin' || user.user_type === 'super-admin';
    return isAdmin || user.id === targetUser.id;
  };

  return (
    <PageLayout title="Staff Directory & In/Out Board" fullWidth>
      <Box sx={{ mt: 3, mb: 3, px: 3, width: '75vw', ml: '-24px', pr: 0 }}>
        <Sheet
          variant="soft"
          sx={{
            borderRadius: 'md',
            p: 2,
            backgroundColor: 'background.level1',
            boxShadow: 'sm',
          }}
        >
          {['In Office', 'Remote', 'In Field', 'Out for the Day', 'Unknown'].map((statusLabel) => {
            const filtered = sortedUsers.filter((u) =>
              statusLabel === 'Unknown'
                ? !u.status || !['In Office', 'Remote', 'In Field', 'Out for the Day'].includes(u.status)
                : u.status === statusLabel
            );
            if (filtered.length === 0) return null;
            return (
              <Box key={statusLabel} sx={{ mb: 4 }}>
                <Typography level="h4" sx={{ mb: 1 }}>
                  {statusLabel === 'In Office'
                    ? '🟢 In Office'
                    : statusLabel === 'Remote'
                    ? '🟠 Remote'
                    : statusLabel === 'In Field'
                    ? '🟠 In Field'
                    : statusLabel === 'Out for the Day'
                    ? '🔴 Out for the Day'
                    : '⚫ Unknown'}
                </Typography>
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                    gap: 3,
                  }}
                >
                  {filtered.map((u) => (
                    <Box key={u.id}>
                      <Sheet
                        variant="outlined"
                        sx={{
                          p: 2,
                          borderRadius: 'md',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                        }}
                      >
                        <Box>
                          <Typography level="title-md">{u.display_name}</Typography>
                          <Typography level="body-sm" textColor="text.secondary">
                            {u.email}
                          </Typography>
                          <Typography level="body-sm">{u.department || '—'}</Typography>
                          {editingUserId === u.id ? (
                            <Textarea
                              minRows={1}
                              maxRows={3}
                              placeholder="Add a note…"
                              value={statusUpdates[`note_${u.id}`] ?? u.status_note ?? ''}
                              onChange={(e) => handleNoteChange(u.id, e.target.value)}
                              sx={{ mt: 1, width: '100%' }}
                            />
                          ) : (
                            u.status_note && (
                              <Typography level="body-sm" sx={{ fontStyle: 'italic' }}>
                                {u.status_note}
                              </Typography>
                            )
                          )}
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {editingUserId === u.id ? (
                            <>
                              <Select
                                value={statusUpdates[u.id] || u.status || ''}
                                onChange={(_, val) =>
                                  setStatusUpdates((prev) => ({ ...prev, [u.id]: val }))
                                }
                                size="sm"
                                sx={{ minWidth: 160 }}
                              >
                                <Option value="In Office">In Office</Option>
                                <Option value="Remote">Remote</Option>
                                <Option value="In Field">In Field</Option>
                                <Option value="Out for the Day">Out for the Day</Option>
                              </Select>
                              <IconButton
                                size="sm"
                                variant="soft"
                                color="success"
                                onClick={() =>
                                  handleStatusChange(
                                    u.id,
                                    statusUpdates[u.id] || u.status || ''
                                  )
                                }
                              >
                                Save
                              </IconButton>
                            </>
                          ) : (
                            <Typography level="body-sm">{u.status || '—'}</Typography>
                          )}
                          {canEdit(u) && (
                            <IconButton
                              size="sm"
                              variant="outlined"
                              onClick={() =>
                                setEditingUserId(editingUserId === u.id ? null : u.id)
                              }
                            >
                              <Edit />
                            </IconButton>
                          )}
                        </Box>
                      </Sheet>
                    </Box>
                  ))}
                </Box>
              </Box>
            );
          })}
        </Sheet>
      </Box>
    </PageLayout>
  );
};

export default Staff;
